import { useEffect, useState } from 'react';

import { GitCompare } from 'lucide-react';

import { Button } from '@/shadcn/button';

import { count } from '@/components/traceInspector/traceInspector.utils';
import { CubeTransparent } from '@/components/ui/customIcons/cubeTransparent';
import { env } from '@/constant/env';
import { References } from '../references/references';
import type { LinkQueryResult } from '../traceInspector.types';
import { DataDiffModal } from './dataDiffModal/dataDiffModal';
import { SegmentBody } from './segmentBody';
import { SegmentEvidence } from './segmentEvidence';
import { WhoDidWhatWhen } from './whoDidWhatWhen/whoDidWhatWhen';

type Props = {
  traceId: string;
  link: LinkQueryResult;
  linkCreatedByAccount: object;
  workflowContext: object;
};

export const SegmentDetail = ({
  traceId,
  link,
  linkCreatedByAccount,
  workflowContext
}: Props) => {
  const [evidencesHidden, setEvidencesHidden] = useState<boolean>(true);
  const [referencesHidden, setReferencesHidden] = useState<boolean>(true);
  const [showDataDiff, setShowDataDiff] = useState<boolean>(false);

  const onEvidenceClick = () => {
    setEvidencesHidden(prevState => !prevState);
  };

  const handleReferencesClick = () => {
    setReferencesHidden(prevState => !prevState);
  };

  useEffect(() => {
    setReferencesHidden(true);
  }, []);

  return (
    <div className="text-card-foreground">
      <div className="mb-1 flex w-full items-center gap-4">
        <div className="flex grow items-center gap-4">
          <div className="font-semibold text-lg">
            {link.action?.title ?? null}
          </div>
          <div className="flex h-5 select-none items-center whitespace-nowrap rounded-lg border-primary-deep/10 bg-primary-deep/5 px-3 py-1 text-primary-deep text-xs">
            Action {count(link.height)}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {env.FEATURE_FLAGS.USE_BLOCKCHAIN_INFO && (
            <div
              aria-expanded={!evidencesHidden}
              className="group/evidenceButton flex cursor-pointer select-none items-center gap-2 text-muted-foreground hover:text-primary"
              onClick={onEvidenceClick}
              title={
                evidencesHidden
                  ? 'Show blockchain info'
                  : 'Hide blockchain info'
              }
            >
              <CubeTransparent className="size-4 text-primary" />
            </div>
          )}
          {env.FEATURE_FLAGS.USE_STATE_DIFF && (
            <Button
              className="group/dataDiffButton flex cursor-pointer select-none items-center gap-2 text-muted-foreground hover:text-primary"
              onClick={() => setShowDataDiff(true)}
              variant="ghost"
            >
              <GitCompare className="size-4 text-primary" /> Show data diff
            </Button>
          )}
        </div>
      </div>
      <div className="flex flex-col gap-4">
        <WhoDidWhatWhen link={link} />
        {env.FEATURE_FLAGS.USE_BLOCKCHAIN_INFO && !evidencesHidden && (
          <div
            aria-hidden={evidencesHidden}
            className="mb-6 h-fit starting:h-0 overflow-hidden opacity-100 starting:opacity-0 transition-all duration-300"
          >
            <SegmentEvidence link={link} />
          </div>
        )}
        <References
          collapsed={referencesHidden}
          handleClick={handleReferencesClick}
          link={link}
          traceId={traceId}
        />
        <section className="mt-2">
          <SegmentBody
            link={link}
            linkCreatedByAccount={linkCreatedByAccount}
            workflowContext={workflowContext}
          />
        </section>
      </div>
      {env.FEATURE_FLAGS.USE_STATE_DIFF && (
        <DataDiffModal
          currentLinkHash={link.linkHash}
          onOpenChange={setShowDataDiff}
          open={showDataDiff}
          prevLinkHash={link.prevLinkHash}
        />
      )}
    </div>
  );
};
